import { redirect } from "next/navigation";
import { auth } from "@/auth";
import type { OrderStatus, Prisma } from "@prisma/client";
import { prisma } from "@/lib/prisma";
import { Container } from "@/components/layout/container";
import { StatusUpdateForm } from "./status-update-form";

function getOrderStatusLabel(status: string) {
  switch (status) {
    case "PENDING":
      return "En attente";
    case "PAID":
      return "Payée";
    case "PREPARING":
      return "En préparation";
    case "SHIPPED":
      return "Expédiée";
    case "COMPLETED":
      return "Terminée";
    case "CANCELLED":
      return "Annulée";
    default:
      return status;
  }
}

function getPaymentStatusLabel(status: string) {
  switch (status) {
    case "PENDING":
      return "En attente";
    case "PAID":
      return "Payé";
    case "FAILED":
      return "Échoué";
    case "REFUNDED":
      return "Remboursé";
    default:
      return status;
  }
}

function getOrderStatusClasses(status: string) {
  switch (status) {
    case "PAID":
      return "bg-[var(--primary)]/10 text-[var(--primary)]";
    case "PREPARING":
      return "bg-orange-100 text-orange-700";
    case "SHIPPED":
      return "bg-sky-100 text-sky-700";
    case "COMPLETED":
      return "bg-emerald-100 text-emerald-700";
    case "CANCELLED":
      return "bg-red-100 text-red-700";
    case "PENDING":
    default:
      return "bg-amber-100 text-amber-700";
  }
}

function getPaymentStatusClasses(status: string) {
  switch (status) {
    case "PAID":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "FAILED":
      return "border-red-200 bg-red-50 text-red-700";
    case "REFUNDED":
      return "border-slate-200 bg-slate-50 text-slate-700";
    case "PENDING":
    default:
      return "border-[var(--border)] bg-white/70 text-[var(--foreground)]/65";
  }
}

export default async function AdminCommandesPage({
  searchParams,
}: {
  searchParams?: Promise<{ q?: string; status?: string }>;
}) {
  const session = await auth();

  if (!session?.user) {
    redirect("/connexion?callbackUrl=/admin/commandes");
  }

  if (session.user.role !== "ADMIN") {
    redirect("/compte");
  }

  const params = await searchParams;
  const query = (params?.q || "").trim();
  const statusFilter = (params?.status || "").trim();

  const normalizedStatus =
    statusFilter && statusFilter !== "ALL"
      ? (statusFilter as OrderStatus)
      : undefined;

  const where: Prisma.OrderWhereInput = {
    ...(normalizedStatus ? { status: normalizedStatus } : {}),
    ...(query
      ? {
          OR: [
            {
              orderNumber: {
                contains: query,
                mode: "insensitive" as const,
              },
            },
            {
              email: {
                contains: query,
                mode: "insensitive" as const,
              },
            },
            {
              customerFirstName: {
                contains: query,
                mode: "insensitive" as const,
              },
            },
            {
              customerLastName: {
                contains: query,
                mode: "insensitive" as const,
              },
            },
            {
              shippingAddressText: {
                contains: query,
                mode: "insensitive" as const,
              },
            },
            {
              items: {
                some: {
                  productNameSnapshot: {
                    contains: query,
                    mode: "insensitive" as const,
                  },
                },
              },
            },
          ],
        }
      : {}),
  };

  const orders = await prisma.order.findMany({
    where,
    include: {
      items: true,
      user: true,
    },
    orderBy: {
      createdAt: "desc",
    },
  });

  const statusCounts = await prisma.order.groupBy({
    by: ["status"],
    _count: {
      status: true,
    },
  });

  const statusCountMap = statusCounts.reduce<Record<string, number>>(
    (acc, entry) => {
      acc[entry.status] = entry._count.status;
      return acc;
    },
    {}
  );

  const statusTabs = [
    {
      value: "ALL",
      label: "Toutes",
      count: Object.values(statusCountMap).reduce((sum, value) => sum + value, 0),
    },
    {
      value: "PENDING",
      label: "En attente",
      count: statusCountMap.PENDING ?? 0,
    },
    {
      value: "PAID",
      label: "Payées",
      count: statusCountMap.PAID ?? 0,
    },
    {
      value: "PREPARING",
      label: "Préparation",
      count: statusCountMap.PREPARING ?? 0,
    },
    {
      value: "SHIPPED",
      label: "Expédiées",
      count: statusCountMap.SHIPPED ?? 0,
    },
    {
      value: "COMPLETED",
      label: "Terminées",
      count: statusCountMap.COMPLETED ?? 0,
    },
    {
      value: "CANCELLED",
      label: "Annulées",
      count: statusCountMap.CANCELLED ?? 0,
    },
  ];

  return (
    <section
      className="section min-h-screen"
      style={{
        background:
          "linear-gradient(180deg, rgba(244,215,231,0.96) 0%, rgba(247,225,235,0.94) 38%, rgba(245,210,227,0.92) 100%)",
      }}
    >
      <Container>
        <div className="mb-10 max-w-3xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/85">
            Administration
          </p>
          <h1 className="mt-4 text-5xl leading-[0.96] sm:text-6xl">
            Gestion des commandes
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-7 text-[var(--foreground)]/74">
            Retrouvez ici toutes les commandes passées sur la boutique, leur statut,
            leur état de paiement et les informations essentielles pour le suivi.
          </p>
        </div>

        <form
          className="mb-8 rounded-[28px] border border-[var(--border)] bg-[var(--card)] p-5 shadow-[var(--shadow)] sm:p-6"
          method="GET"
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label
                htmlFor="q"
                className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--foreground)]/55"
              >
                Recherche
              </label>
              <input
                id="q"
                name="q"
                defaultValue={query}
                placeholder="Commande, email, cliente, produit..."
                className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 text-sm outline-none transition focus:border-[var(--primary)]"
              />
            </div>

            <div className="w-full lg:w-[240px]">
              <label
                htmlFor="status"
                className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--foreground)]/55"
              >
                Statut
              </label>
              <select
                id="status"
                name="status"
                defaultValue={statusFilter || "ALL"}
                className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 text-sm outline-none transition focus:border-[var(--primary)]"
              >
                <option value="ALL">Tous les statuts</option>
                <option value="PENDING">En attente</option>
                <option value="PAID">Payée</option>
                <option value="PREPARING">En préparation</option>
                <option value="SHIPPED">Expédiée</option>
                <option value="COMPLETED">Terminée</option>
                <option value="CANCELLED">Annulée</option>
              </select>
            </div>

            <div className="flex gap-3 lg:shrink-0">
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-full bg-[var(--primary)] px-5 py-3 text-sm font-medium text-white transition hover:opacity-90"
              >
                Filtrer
              </button>

              <a
                href="/admin/commandes"
                className="inline-flex items-center justify-center rounded-full border border-[var(--border)] px-5 py-3 text-sm font-medium transition hover:border-[var(--primary)] hover:text-[var(--primary)]"
              >
                Réinitialiser
              </a>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-[var(--foreground)]/65">
            <span className="rounded-full bg-[var(--card)] px-3 py-1">
              {orders.length} commande{orders.length > 1 ? "s" : ""}
            </span>
            {query ? (
              <span className="rounded-full border border-[var(--border)] px-3 py-1">
                Recherche : {query}
              </span>
            ) : null}
            {statusFilter && statusFilter !== "ALL" ? (
              <span className="rounded-full border border-[var(--border)] px-3 py-1">
                Statut : {getOrderStatusLabel(statusFilter)}
              </span>
            ) : null}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {statusTabs.map((tab) => {
              const isActive =
                (statusFilter || "ALL") === tab.value ||
                (!statusFilter && tab.value === "ALL");

              const href =
                tab.value === "ALL"
                  ? query
                    ? `/admin/commandes?q=${encodeURIComponent(query)}`
                    : "/admin/commandes"
                  : query
                    ? `/admin/commandes?q=${encodeURIComponent(query)}&status=${tab.value}`
                    : `/admin/commandes?status=${tab.value}`;

              return (
                <a
                  key={tab.value}
                  href={href}
                  className={[
                    "inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm transition",
                    isActive
                      ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]"
                      : "border-[var(--border)] bg-[var(--card)] text-[var(--foreground)]/68 hover:border-[var(--primary)]/35 hover:text-[var(--primary)]",
                  ].join(" ")}
                >
                  <span>{tab.label}</span>
                  <span className="rounded-full bg-[var(--card)] px-2 py-0.5 text-[11px]">
                    {tab.count}
                  </span>
                </a>
              );
            })}
          </div>
        </form>

        {orders.length === 0 ? (
          <div className="rounded-[30px] border border-[var(--border)] bg-[var(--card)] p-8 shadow-[var(--shadow)] sm:p-10">
            <h2 className="text-3xl">
              {query || (statusFilter && statusFilter !== "ALL")
                ? "Aucun résultat"
                : "Aucune commande"}
            </h2>
            <p className="mt-4 text-sm leading-7 text-[var(--foreground)]/72">
              {query || (statusFilter && statusFilter !== "ALL")
                ? "Aucune commande ne correspond à votre recherche ou à ce filtre pour le moment."
                : "Les commandes apparaîtront ici après les premiers paiements validés."}
            </p>
          </div>
        ) : (
          <div className="grid gap-5">
            {orders.map((order) => (
              <details
                key={`${order.id}-${order.status}`}
                className="group rounded-[28px] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow)] transition open:bg-[var(--card)]"
              >
                <summary className="flex cursor-pointer list-none flex-col gap-5 p-6 marker:content-none sm:p-7 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
                      <p className="text-sm font-semibold tracking-[0.01em] text-[var(--foreground)]/85">
                        {order.orderNumber}
                      </p>

                      <span
                        className={`rounded-full px-3 py-1 text-[11px] font-medium ${getOrderStatusClasses(order.status)}`}
                      >
                        {getOrderStatusLabel(order.status)}
                      </span>

                      <span
                        className={`rounded-full border px-3 py-1 text-[11px] ${getPaymentStatusClasses(order.paymentStatus)}`}
                      >
                        Paiement : {getPaymentStatusLabel(order.paymentStatus)}
                      </span>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-3 text-sm text-[var(--foreground)]/65">
                      <span>{order.customerFirstName} {order.customerLastName}</span>
                      <span>•</span>
                      <span>{new Date(order.createdAt).toLocaleDateString("fr-FR")}</span>
                      <span>•</span>
                      <span>{Number(order.total).toFixed(2)} €</span>
                    </div>
                  </div>

                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--card)] text-[var(--foreground)]/65 transition group-open:rotate-180 group-open:border-[var(--primary)] group-open:text-[var(--primary)]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </span>
                </summary>

                <div className="border-t border-[var(--border)]/70 px-6 pb-6 pt-5 sm:px-7 sm:pb-7">
                  <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 xl:gap-4">
                        <InfoLine
                          label="Cliente"
                          value={`${order.customerFirstName} ${order.customerLastName}`}
                        />
                        <InfoLine label="Email" value={order.email} />
                        <InfoLine
                          label="Téléphone"
                          value={order.customerPhone || "Non renseigné"}
                        />
                        <InfoLine
                          label="Date"
                          value={new Date(order.createdAt).toLocaleDateString("fr-FR")}
                        />
                        <InfoLine
                          label="Total"
                          value={`${Number(order.total).toFixed(2)} €`}
                        />
                        <InfoLine
                          label="Adresse de livraison"
                          value={order.shippingAddressText || "Non renseignée"}
                        />
                      </div>

                      <div className="mt-5 flex flex-wrap gap-2.5">
                        {order.items.map((item) => (
                          <span
                            key={item.id}
                            className="rounded-full border border-[var(--border)] bg-[var(--card)] px-3 py-1 text-xs text-[var(--foreground)]/72"
                          >
                            {item.productNameSnapshot} × {item.quantity}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="w-full rounded-[24px] border border-[var(--border)] bg-[var(--card)] p-4 lg:w-[280px] lg:self-start">
                      <StatusUpdateForm
                        orderId={order.id}
                        defaultStatus={order.status}
                        orderNumber={order.orderNumber}
                      />
                    </div>
                  </div>
                </div>
              </details>
            ))}
          </div>
        )}
      </Container>
    </section>
  );
}

function InfoLine({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-[18px] border border-[var(--border)] bg-[var(--card)] p-4 transition hover:border-[var(--primary)]/25">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--foreground)]/55">
        {label}
      </p>
      <p className="mt-2 whitespace-pre-line break-words text-sm leading-6 text-[var(--foreground)]/78">
        {value}
      </p>
    </div>
  );
}